/**
 * SSE (Server-Sent Events) parser.
 *
 * Parses raw bytes from a ReadableStream into structured SSE events
 * following the W3C EventSource specification:
 * https://html.spec.whatwg.org/multipage/server-sent-events.html
 *
 * Replaces both the manual parsing in use-sse-stream.ts and the
 * {@link https://github.com/Azure/fetch-event-source | @microsoft/fetch-event-source} dependency.
 */

import type { IParsedSSEEvent } from '../types';

/**
 * Incrementally parses SSE-formatted text and yields complete events.
 *
 * SSE events are separated by blank lines (`\n\n`). Each event can have
 * `data:`, `id:`, `event:`, and `retry:` fields. This parser handles
 * partial chunks from a ReadableStream correctly.
 */
export class SSEParser {
  private buffer = '';

  /**
   * Feeds a text chunk into the parser and returns any complete events.
   *
   * @param chunk - Raw text from the stream (may contain partial events)
   * @returns Array of fully parsed SSE events (may be empty)
   */
  push(chunk: string): IParsedSSEEvent[] {
    this.buffer += chunk;
    const events: IParsedSSEEvent[] = [];

    const parts = this.buffer.split('\n\n');
    // Last part is incomplete — keep it in the buffer
    this.buffer = parts.pop() ?? '';

    for (const part of parts) {
      if (!part.trim()) continue;

      const event = this.parseEvent(part);
      if (event) {
        events.push(event);
      }
    }

    return events;
  }

  /**
   * Resets internal buffer state. Call when reconnecting.
   */
  reset(): void {
    this.buffer = '';
  }

  /**
   * Parses a single SSE event block (text between blank lines).
   *
   * @param raw - Raw event text without the trailing double newline
   * @returns Parsed event or null if the block contains no data
   */
  private parseEvent(raw: string): IParsedSSEEvent | null {
    let data = '';
    let id: string | undefined;
    let event: string | undefined;
    const dataLines: string[] = [];

    for (const line of raw.split('\n')) {
      if (line.startsWith(':')) {
        // Comment line — skip
        continue;
      }

      const colonIdx = line.indexOf(':');

      if (colonIdx === -1) {
        // Field name only, no value — treat as empty value per spec
        continue;
      }

      const field = line.substring(0, colonIdx);
      // Value starts after `:` and optional single space
      let value = line.substring(colonIdx + 1);
      if (value.startsWith(' ')) {
        value = value.substring(1);
      }

      switch (field) {
        case 'data':
          dataLines.push(value);
          break;
        case 'id':
          id = value;
          break;
        case 'event':
          event = value;
          break;
        // `retry:` is handled at the connection level, not per-event
      }
    }

    if (dataLines.length === 0) {
      return null;
    }

    data = dataLines.join('\n');

    return { data, id, event };
  }
}
